var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../../../Infra/RemoteData";
import { ProjectStaticPageName } from "healthex-common/project.data.dtos";
import { SimpleDialogModel } from "../../SimpleDialogModel";
import { FormModel } from "../../FormModel";
import { ProjectType, StudyAccessData, StudyStatus, StudyType } from "healthex-common/study.dtos";
import { getFullName, willNeverHappen } from "healthex-common/utils";
import { format } from "date-fns";
export class AddProjectSidePanelModel {
    constructor(container) {
        this.editRequirementsFormModel = new FormModel({
            projectType: null,
            hipaaOrganizationLegalName: null,
            hipaaOrganizationLegalNameConfirmed: false,
            studyType: null,
            accessData: null,
            isSponsor: null,
        }, [], (input) => {
            this.didEditRequirements = true;
            if (input.hipaaOrganizationLegalName) {
                this.editRequirementsFormModel.input.hipaaOrganizationLegalNameConfirmed = false;
            }
        });
        this.confirmDeleteDialog = new SimpleDialogModel();
        this.studyRequirementsDialog = new SimpleDialogModel();
        this.editStudyRequirementsDialog = new SimpleDialogModel();
        this.editStudyRequirementsConfirmCloseDialog = new SimpleDialogModel();
        this.isSavingRequirements = false;
        this.didEditRequirements = false;
        this.activateProjectDialog = new SimpleDialogModel();
        this.activateProjectConfirmed = false;
        this.activateProjectVersionName = "";
        this.authManager = container.authManager;
        this.appUIManager = container.appUIManager;
        this.welcomeUserUIManager = container.welcomeUserUIManager;
        this.addProjectUIManager = container.addProjectUIManager;
        this.studyManager = container.studyManager;
        this.addProjectRequirementsWizardUIManager = container.addProjectRequirementsWizardUIManager;
    }
    get organizationLogoUrl() {
        var _a, _b;
        return ((_a = this.authManager.orgLogoUrl) !== null && _a !== void 0 ? _a : (this.welcomeUserUIManager.organization !== NOT_LOADED && ((_b = this.welcomeUserUIManager.organization) === null || _b === void 0 ? void 0 : _b.logoUrl)));
    }
    get projectPages() {
        return this.addProjectUIManager.projectPages;
    }
    get currentPage() {
        return this.addProjectUIManager.currentPage;
    }
    get currentProjectPageNameRoute() {
        return this.addProjectUIManager.currentProjectPageNameRoute;
    }
    get currentProjectSectionName() {
        return this.addProjectUIManager.currentProjectSectionName;
    }
    get overallProgress() {
        return this.addProjectUIManager.overallProgress;
    }
    get isProjectSettingsCompleted() {
        return this.addProjectUIManager.isProjectSettingsCompleted;
    }
    get isPatientExperienceCompleted() {
        return this.addProjectUIManager.isPatientExperienceCompleted;
    }
    get detectedEnglishTranslationUpdate() {
        return this.addProjectUIManager.detectedEnglishTranslationUpdate;
    }
    get navSectionOffset() {
        return this.addProjectUIManager.navSectionOffset;
    }
    get hasTranslationsAndAreCompleted() {
        return this.addProjectUIManager.hasTranslationsAndAreCompleted;
    }
    get arePDFDownloadsReady() {
        return this.addProjectUIManager.arePDFDownloadsReady;
    }
    get canUpdatePDFDownloads() {
        return this.addProjectUIManager.canUpdatePDFDownloads;
    }
    get bottomMenu() {
        const translationsEnabled = this.isPatientExperienceCompleted;
        const translationsCompleted = this.hasTranslationsAndAreCompleted;
        const translationsItem = {
            page: ProjectStaticPageName.TRANSLATIONS,
            text: "Translations",
            action: async () => await this.goToPage(ProjectStaticPageName.TRANSLATIONS),
            selected: this.currentPage === ProjectStaticPageName.TRANSLATIONS,
            enabled: translationsEnabled,
            showHoverBackground: translationsEnabled,
            completed: translationsCompleted,
            locked: !translationsEnabled,
            warning: this.detectedEnglishTranslationUpdate,
            chipText: translationsEnabled && !translationsCompleted ? "UNLOCKED" : undefined,
            tooltip: !translationsEnabled ? "Please complete the Patient Experience to translate it into other languages." : undefined,
        };
        const previewEnabled = this.isProjectSettingsCompleted && this.isPatientExperienceCompleted;
        const pdfDownloadsReady = this.arePDFDownloadsReady;
        const previewItem = {
            page: ProjectStaticPageName.PREVIEW,
            text: "Preview and download",
            action: async () => await this.goToPage(ProjectStaticPageName.PREVIEW),
            selected: this.currentPage === ProjectStaticPageName.PREVIEW,
            enabled: previewEnabled,
            showHoverBackground: previewEnabled,
            completed: pdfDownloadsReady,
            locked: !previewEnabled,
            warning: this.canUpdatePDFDownloads,
            chipText: previewEnabled && !pdfDownloadsReady ? "UNLOCKED" : undefined,
            tooltip: !previewEnabled ? "Please complete the Project Details and Patient Experience to preview and download it." : undefined,
        };
        return [translationsItem, previewItem];
    }
    navigateToHome() {
        const homepage = this.appUIManager.homepage;
        if (homepage === null)
            return;
        this.appUIManager.navigateToPage(homepage);
    }
    async goToPage(pageName, sectionName) {
        await this.addProjectUIManager.goToPage(pageName, sectionName);
    }
    setCurrentSection(sectionName) {
        this.addProjectUIManager.setCurrentSection(sectionName);
    }
    get isPdda() {
        var _a;
        if (this.study === NOT_LOADED)
            return false;
        return !!((_a = this.study) === null || _a === void 0 ? void 0 : _a.pdda);
    }
    get allowInterventionalStudyTypeRequirement() {
        return !this.isPdda;
    }
    get showAccessDataRequirement() {
        return !this.isPdda;
    }
    get showStudyTypeRequirement() {
        return this.editRequirementsFormModel.input.projectType !== ProjectType.GENERAL_DATA_USE;
    }
    get showSponsorRequirement() {
        return this.editRequirementsFormModel.input.projectType !== ProjectType.GENERAL_DATA_USE;
    }
    get showProjectTypeRequirement() {
        return false;
    }
    get showLegalRequirements() {
        return this.isPdda;
    }
    get study() {
        return this.addProjectUIManager.study;
    }
    get projectType() {
        var _a;
        if (this.study === NOT_LOADED || !this.study)
            return null;
        return (_a = this.study.projectType) !== null && _a !== void 0 ? _a : null;
    }
    get studyOwner() {
        if (this.study === NOT_LOADED || !this.study)
            return null;
        return getFullName(this.study.owner);
    }
    get studyType() {
        if (this.study === NOT_LOADED || !this.study)
            return null;
        switch (this.study.studyType) {
            case StudyType.INTERVENTIONAL:
                return `Interventional Study`;
            case StudyType.REGISTRY:
                return `Patient Registry`;
            case StudyType.OTHER:
                return `Observational Study`;
        }
        return willNeverHappen(this.study.studyType);
    }
    get requiresIdentifiedPatientData() {
        if (this.study === NOT_LOADED || !this.study)
            return null;
        return this.study.accessData === StudyAccessData.FULL_RECORD_DEIDENTIFIED ? "No" : "Yes";
    }
    get projectTypeReadOnlyText() {
        if (this.study === NOT_LOADED || !this.study || !this.projectType)
            return null;
        switch (this.projectType) {
            case ProjectType.GENERAL_DATA_USE:
                return "Patient Health Record Access";
        }
        return willNeverHappen(this.projectType);
    }
    get hipaaOrganizationLegalName() {
        if (this.study === NOT_LOADED || !this.study)
            return null;
        return this.study.hipaaOrganizationLegalName;
    }
    get isSponsoredStudyText() {
        if (this.study === NOT_LOADED || !this.study)
            return null;
        if (this.study.isSponsor === null || this.study.isSponsor === undefined)
            return "Not specified";
        return this.study.isSponsor ? "Yes" : "No";
    }
    get isSavedButtonEnabled() {
        const formModelInput = this.editRequirementsFormModel.input;
        return (this.didEditRequirements &&
            formModelInput.studyType !== null &&
            (this.showAccessDataRequirement ? formModelInput.accessData !== null : true) &&
            (this.showSponsorRequirement ? formModelInput.isSponsor !== null : true) &&
            (this.showLegalRequirements ? !!formModelInput.hipaaOrganizationLegalName : true) &&
            (this.showLegalRequirements ? formModelInput.hipaaOrganizationLegalNameConfirmed : true));
    }
    get isSaveButtonLoading() {
        return this.isSavingRequirements;
    }
    get addProjectDialogTitleInfo() {
        return this.addProjectRequirementsWizardUIManager.dialogTitleInfo;
    }
    get activateProjectEnabled() {
        return this.addProjectUIManager.isOverallCompleted;
    }
    get activateProjectButtonText() {
        var _a;
        return this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.status) === StudyStatus.DRAFT ? "Confirm and activate project" : "Publish changes";
    }
    get dialogActivateProjectButtonText() {
        var _a;
        return this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.status) === StudyStatus.DRAFT
            ? "Confirm and activate project"
            : "Confirm and publish changes";
    }
    loadEditStudyRequirementsForm() {
        var _a, _b, _c, _d, _e, _f;
        if (this.study === NOT_LOADED || !this.study)
            return;
        this.editRequirementsFormModel.input = {
            studyType: (_a = this.study.studyType) !== null && _a !== void 0 ? _a : null,
            accessData: (_b = this.study.accessData) !== null && _b !== void 0 ? _b : null,
            isSponsor: (_c = this.study.isSponsor) !== null && _c !== void 0 ? _c : null,
            projectType: (_d = this.study.projectType) !== null && _d !== void 0 ? _d : null,
            hipaaOrganizationLegalName: (_e = this.study.hipaaOrganizationLegalName) !== null && _e !== void 0 ? _e : null,
            hipaaOrganizationLegalNameConfirmed: (_f = this.study.hipaaOrganizationLegalNameConfirmed) !== null && _f !== void 0 ? _f : false,
        };
    }
    showStudyRequirementsDialog() {
        this.loadEditStudyRequirementsForm();
        this.studyRequirementsDialog.show();
    }
    showEditStudyRequirementsDialog() {
        this.didEditRequirements = false;
        this.studyRequirementsDialog.onChoice(false);
        this.loadEditStudyRequirementsForm();
        this.editStudyRequirementsDialog.show();
    }
    async closeEditStudyRequirementsDialog() {
        if (this.didEditRequirements) {
            const confirmed = await this.editStudyRequirementsConfirmCloseDialog.showAndWait();
            if (!confirmed)
                return;
        }
        this.editStudyRequirementsDialog.open = false;
        this.editRequirementsFormModel.input = {
            studyType: null,
            accessData: null,
            isSponsor: null,
            projectType: null,
            hipaaOrganizationLegalName: null,
            hipaaOrganizationLegalNameConfirmed: false,
        };
    }
    async doSave() {
        var _a;
        if (this.study === NOT_LOADED || !this.study)
            return;
        if (this.isSavingRequirements)
            return;
        this.isSavingRequirements = true;
        const studyType = this.editRequirementsFormModel.input.studyType;
        const accessData = (_a = this.editRequirementsFormModel.input.accessData) !== null && _a !== void 0 ? _a : this.study.accessData;
        const hipaaOrganizationLegalName = this.editRequirementsFormModel.input.hipaaOrganizationLegalName;
        const hipaaOrganizationLegalNameConfirmed = this.editRequirementsFormModel.input.hipaaOrganizationLegalNameConfirmed;
        if (this.authManager.id === null || studyType === null || accessData === null) {
            this.isSavingRequirements = false;
            return;
        }
        const isSponsor = this.editRequirementsFormModel.input.isSponsor;
        const saveInput = {
            studyType,
            ...(this.showAccessDataRequirement ? { accessData } : {}),
            ...(this.showSponsorRequirement ? { isSponsor } : {}),
            ...(this.showLegalRequirements ? { hipaaOrganizationLegalName, hipaaOrganizationLegalNameConfirmed } : {}),
        };
        try {
            await this.studyManager.updateStudyVersion(this.study.id, saveInput);
            await this.addProjectUIManager.refreshStudy({ preserveDirty: true });
        }
        finally {
            this.isSavingRequirements = false;
            this.editStudyRequirementsDialog.open = false;
            this.addProjectUIManager.didCloseTranslationsRequirementsChangedBanner = false;
        }
    }
    get editRequirementsButtonEnabled() {
        var _a;
        return this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.status) === StudyStatus.DRAFT;
    }
    get deleteProjectButtonEnabled() {
        var _a;
        return this.study !== NOT_LOADED && ((_a = this.study) === null || _a === void 0 ? void 0 : _a.status) === StudyStatus.DRAFT;
    }
    async deleteProject() {
        const confirmed = await this.confirmDeleteDialog.showAndWait();
        if (confirmed) {
            await this.addProjectUIManager.deleteStudyDraft();
        }
    }
    get activateProjectContinueEnabled() {
        return this.activateProjectConfirmed && this.activateProjectVersionName !== "";
    }
    get activateProjectDialogTitle() {
        if (this.study === NOT_LOADED || !this.study)
            return "";
        return this.study.status === StudyStatus.ACTIVE
            ? "Before publishing the changes, please confirm the following."
            : "Before activating the project, please confirm the following.";
    }
    showActivateProjectDialog() {
        if (this.study === NOT_LOADED || !this.study)
            return;
        if (this.addProjectUIManager.versionNumber !== NOT_LOADED && this.addProjectUIManager.versionNumber !== null) {
            this.activateProjectVersionName = `Version ${this.addProjectUIManager.versionNumber} (${format(new Date(), "LLL dd, yyyy")})`;
        }
        this.activateProjectDialog.show();
    }
    get activateProjectVersionNameVisible() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        return this.study.status === StudyStatus.ACTIVE;
    }
    closeActivateProjectDialog() {
        this.activateProjectDialog.open = false;
    }
    async activateProject() {
        await this.addProjectUIManager.confirmAndAddStudy(this.activateProjectVersionName);
        this.activateProjectDialog.open = false;
    }
}
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "organizationLogoUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "projectPages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "currentPage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "currentProjectPageNameRoute", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "currentProjectSectionName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "overallProgress", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isProjectSettingsCompleted", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isPatientExperienceCompleted", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "detectedEnglishTranslationUpdate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "navSectionOffset", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "hasTranslationsAndAreCompleted", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "arePDFDownloadsReady", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "canUpdatePDFDownloads", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "bottomMenu", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddProjectSidePanelModel.prototype, "isSavingRequirements", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddProjectSidePanelModel.prototype, "didEditRequirements", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isPdda", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "allowInterventionalStudyTypeRequirement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "showAccessDataRequirement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "showStudyTypeRequirement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "showSponsorRequirement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "showProjectTypeRequirement", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "showLegalRequirements", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "projectType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "studyOwner", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "studyType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "requiresIdentifiedPatientData", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "projectTypeReadOnlyText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "hipaaOrganizationLegalName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isSponsoredStudyText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isSavedButtonEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "isSaveButtonLoading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "addProjectDialogTitleInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "activateProjectEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "activateProjectButtonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "dialogActivateProjectButtonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "editRequirementsButtonEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "deleteProjectButtonEnabled", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddProjectSidePanelModel.prototype, "activateProjectConfirmed", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], AddProjectSidePanelModel.prototype, "activateProjectVersionName", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "activateProjectContinueEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "activateProjectDialogTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], AddProjectSidePanelModel.prototype, "activateProjectVersionNameVisible", null);
